import * as React from "react";
import LogRocket from "logrocket";
import VisuallyHidden from "@reach/visually-hidden";
import { Layout } from "../Layout";
import { EmpathyController } from "./EmpathyController";
import { ReactComponent as IllustrationActivity } from "@sparkademy/app-common/materials/illustrations/activity.svg";
import { ReactComponent as IllustrationPerson } from "@sparkademy/app-common/materials/illustrations/person.svg";
import { ReactComponent as IllustrationIdea } from "@sparkademy/app-common/materials/illustrations/idea.svg";

import { Container, ContainerSmall } from "@sparkademy/app-common/elements/Container";
import { themeNew } from "@sparkademy/app-common/materials/theme";
import { ThemeProvider, Box, Heading, Label, Text } from "theme-ui";
import { Button, ButtonLink } from "@sparkademy/app-common/elements/Button";
import { uiCopy } from "../../data/ui-copy";
import { Narrative } from "@sparkademy/app-common/elements/Narrative";
import {
  empathyTestStatus,
  empathyMatchingEmotionCreate,
  empathyUnderstandEmotionsCreate,
  empathyUnderstandEmotionsFinish,
  empathyMatchingEmotionFinish,
  empathyImplementEmotionsCreate,
  empathyImplementEmotionsFinish,
} from "../../services/http-api-service";
import { TrackingService } from "../../services/tracking-service";
import { Input } from "@sparkademy/app-common/elements/Input";
import useEventListener from "@use-it/event-listener";
import { useQuery } from "@sparkademy/app-common/utils/useQuery";
import isEmail from "@sparkademy/app-common/utils/isEmail";
import { useHistory } from "react-router-dom";
import { Checkbox } from "@sparkademy/app-common/elements/Checkbox";
import { PrivacyTermsCheckbox } from "../PrivacyTermsCheckbox";
import { HubspotService } from "../../services/hubspot-service";
import { IubendaService } from "../../services/iubenda-service";
import { ProgressBar } from "@sparkademy/app-common/components/ProgressBar";

enum Screens {
  Email = "Email",
  TestAlreadyTaken = "TestAlreadyTaken",
  ContinueTest = "ContinueTest",
  Explanation = "Explanation",
  EmotionMatching = "EmotionMatching",
  UnderstandingEmotions = "UnderstandingEmotions",
  ImplementEmotions = "ImplementEmotions",
  End = "End",
}

export const EmpathyTestLite: React.FC = () => {
  const history = useHistory();

  const [isEmailValid, setIsEmailValid] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [screen, setScreen] = React.useState<Screens>(
    email !== "" ? Screens.Explanation : Screens.Email
  );
  const [resumeTestScreen, setResumeTestScreen] = React.useState<Screens | null>(null);
  const [termsConsentGiven, setTermsConsentGiven] = React.useState<boolean>(false);
  const [marketingConsent, setMarketingConsent] = React.useState<boolean>(false);
  const [currentLevel, setCurrentLevel] = React.useState<number>(1);

  const query = useQuery();
  const cohort = query.get("cohort") || "sparkchecklite";
  const skipTo = query.get("skipTo");

  const onSubmitEmail = React.useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      event && event.preventDefault();

      const testStatus = await empathyTestStatus(email, cohort);

      if (testStatus["understanding_emotions"] === false) {
        setScreen(Screens.Explanation);
        setCurrentLevel(2);
      } else if (testStatus["emotion_matching"] === false) {
        setResumeTestScreen(Screens.EmotionMatching);
        setScreen(Screens.ContinueTest);
        setCurrentLevel(2);
      } else {
        TrackingService.attemptToRetakeSparkCheck(email, "empathy_test");
        history.push("/error?reason=spark-check-taken");
      }

      if (marketingConsent) {
        HubspotService.submitSparkCheckConsent(email, termsConsentGiven, marketingConsent);
      }

      IubendaService.submitConsent(testStatus.user_id, email, {
        location: "spark-check-lite",
        cohort,
      });
      localStorage.setItem("gamblingTestEmail", email);
      LogRocket.identify(email);
    },
    [email, history, termsConsentGiven, marketingConsent, cohort]
  );

  React.useEffect(() => {
    const isValid = isEmail(email);
    setIsEmailValid(isValid);
  }, [email]);

  React.useEffect(() => {
    if (!skipTo) {
      return;
    }

    if (Object.values(Screens).includes(skipTo as Screens)) {
      setScreen(skipTo as Screens);
    }
  }, [skipTo]);

  useEventListener("keydown", (event: KeyboardEvent) => {
    // @ts-ignore
    if (screen === Screens.Explanation && event.keyCode === 13) {
      nextScreen();
    }
  });

  function nextScreen() {
    switch (screen) {
      case Screens.Email:
        setScreen(Screens.Explanation);
        break;
      case Screens.Explanation:
        setScreen(Screens.UnderstandingEmotions);
        break;
      case Screens.UnderstandingEmotions:
        setScreen(Screens.EmotionMatching);
        break;
      case Screens.EmotionMatching:
        setScreen(Screens.End);
        break;
      default:
        break;
    }
  }

  return (
    <ThemeProvider theme={themeNew}>
      <Layout
        sx={{
          bg: "new.primary.white",
        }}
        logoIsClickable={false}
        hideFooter
      >
        <ContainerSmall
          sx={{
            position: "relative",
            pt: [5, "64px"],
            pb: 7,
            width: ["100%", "894px"],
          }}
        >
          <Box sx={{ width: ["95%", "348px"], m: ["20px 10px 48px", "0 auto 48px"] }}>
            <ProgressBar level={currentLevel} numLevels={3} />
          </Box>
          {screen === Screens.Email ? (
            <Box sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  mx: "auto",
                  width: 170,
                  height: 170,
                  svg: {
                    width: "100%",
                    height: "auto",
                  },
                }}
              >
                <IllustrationIdea />
              </Box>

              <Heading
                as="h1"
                sx={{
                  fontSize: [2, 3],
                  fontWeight: "bold",
                  textAlign: "center",
                  mb: 10,
                }}
              >
                Welcome to Spark Check Lite
              </Heading>
              <Narrative>
                Spark Check Lite consists of two parts and requires 15 minutes to complete.
                <br />
                Please put in your email address and click “Next” to start.
              </Narrative>
              <form onSubmit={onSubmitEmail}>
                <Box
                  sx={{
                    mt: 10,
                  }}
                >
                  <VisuallyHidden>
                    <label htmlFor="email">Email Address</label>
                  </VisuallyHidden>
                  <Input
                    type="email"
                    value={email}
                    onChange={event => setEmail(event.currentTarget.value)}
                    placeholder="Email Address"
                    sx={{
                      width: [330, 500],
                      mb: 10,
                      ":focus": {
                        borderColor:
                          email.trim().length > 0
                            ? isEmailValid
                              ? "new.advancedGreen"
                              : "new.failingRed"
                            : undefined,
                      },
                    }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <PrivacyTermsCheckbox
                      consentGiven={termsConsentGiven}
                      setConsentGiven={setTermsConsentGiven}
                    />

                    <Label
                      mb={8}
                      sx={{
                        fontSize: "14px",
                        maxWidth: "65ch",
                        lineHeight: "22px",
                        textAlign: "left",
                      }}
                    >
                      <Checkbox
                        id="marketing"
                        type="checkbox"
                        checked={marketingConsent}
                        onChange={() => setMarketingConsent(!marketingConsent)}
                        wrapperSx={{ mt: 2 }}
                      />
                      <Text sx={{ mt: 0, ml: 0 }}>
                        I agree to receive marketing communications from Sparkademy.
                      </Text>
                    </Label>
                  </Box>
                </Box>
                <Box sx={{ mt: 10 }} />
                <Button type="submit" disabled={!isEmailValid || !termsConsentGiven}>
                  Next
                </Button>
              </form>
            </Box>
          ) : null}

          {screen === Screens.ContinueTest && (
            <Container
              sx={{
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  width: 170,
                  height: 170,
                  svg: {
                    width: "100%",
                    height: "auto",
                  },
                }}
              >
                <IllustrationPerson />
              </Box>
              <Heading
                as="h1"
                sx={{
                  fontSize: [2, 3],
                  fontWeight: "bold",
                  textAlign: "center",
                  mb: 10,
                }}
              >
                Welcome Back
              </Heading>
              <Narrative>
                You exited the test early, but we caught you :) Click the button below to continue
                from where you left off.
              </Narrative>
              <Box sx={{ mt: 10 }} />
              <Button onClick={() => resumeTestScreen && setScreen(resumeTestScreen)}>
                Continue Test
              </Button>
              <Box sx={{ mt: 5 }}>{uiCopy.GENERIC_HELP_MESSAGE()}</Box>
            </Container>
          )}

          {screen === Screens.UnderstandingEmotions ? (
            <EmpathyController
              sectionId={1}
              sectionName={Screens.UnderstandingEmotions}
              introTitle={uiCopy.EMPATHY_UNDERSTAND_EMOTIONS_INTRO_TITLE}
              introBody={uiCopy.EMPATHY_UNDERSTAND_EMOTIONS_INTRO_BODY}
              load={async () => await empathyUnderstandEmotionsCreate()}
              save={async answers => await empathyUnderstandEmotionsFinish(email, cohort, answers)}
              onEnd={nextScreen}
              cohort={cohort}
            />
          ) : null}

          {screen === Screens.EmotionMatching ? (
            <EmpathyController
              sectionId={2}
              sectionName={Screens.EmotionMatching}
              introTitle={uiCopy.EMPATHY_MATCHING_EMOTION_INTRO_TITLE}
              introBody={uiCopy.EMPATHY_MATCHING_EMOTION_INTRO_BODY}
              load={async () => await empathyMatchingEmotionCreate()}
              save={async answers => await empathyMatchingEmotionFinish(email, cohort, answers)}
              onEnd={nextScreen}
              cohort={cohort}
            />
          ) : null}

          {screen === Screens.ImplementEmotions ? (
            <EmpathyController
              sectionId={3}
              sectionName={Screens.ImplementEmotions}
              introTitle={uiCopy.EMPATHY_IMPLEMENT_EMOTIONS_INTRO_TITLE}
              introBody={uiCopy.EMPATHY_IMPLEMENT_EMOTIONS_INTRO_BODY}
              load={async () => await empathyImplementEmotionsCreate()}
              save={async answers => await empathyImplementEmotionsFinish(email, cohort, answers)}
              onEnd={nextScreen}
              cohort={cohort}
            />
          ) : null}

          {screen === Screens.Explanation ? (
            <React.Fragment>
              <Container
                sx={{
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    width: 170,
                    height: 170,
                    svg: {
                      width: "100%",
                      height: "auto",
                    },
                  }}
                >
                  <IllustrationActivity />
                </Box>
                <Heading
                  as="h1"
                  sx={{
                    fontSize: [2, 3],
                    fontWeight: "bold",
                    textAlign: "center",
                    mb: 10,
                  }}
                >
                  Part 1
                </Heading>
                <Narrative>
                  <b>Your goal in this part is to answer 8 multiple choice questions</b> regarding
                  people’s emotions as best as you can. Click Start to begin Part 1.
                </Narrative>
                <Box sx={{ mt: 10 }} />
                <Button onClick={nextScreen}>Start Part 1</Button>
              </Container>
            </React.Fragment>
          ) : null}

          {screen === Screens.End && (
            <React.Fragment>
              <Container
                sx={{
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    width: 170,
                    height: 170,
                    svg: {
                      width: "100%",
                      height: "auto",
                    },
                  }}
                >
                  <IllustrationPerson />
                </Box>
                <Heading
                  as="h1"
                  sx={{
                    fontSize: [2, 3],
                    fontWeight: "bold",
                    textAlign: "center",
                    mb: 10,
                  }}
                >
                  Congratulations!
                </Heading>
                <Narrative>
                  <p>
                    You have completed the first part of the Spark Check Lite!
                    <br />
                    To continue to the second part of Spark Check Lite, click “Next”.
                  </p>
                </Narrative>

                <ButtonLink sx={{ mt: 11 }} to={`/spark-check-part2${window.location.search}`}>
                  Next
                </ButtonLink>
              </Container>
            </React.Fragment>
          )}
        </ContainerSmall>
      </Layout>
    </ThemeProvider>
  );
};
